import {AssetFolderEntityType, AssetType} from 'common/src/modules/Assets/Assets.enums';
import {
  BlockingWidgetDisplayMode,
  CopilotWidgetType,
  ToolName,
} from 'common/src/modules/shared/copilot/copilot.enums';
import {ToolExecuteParams, ToolResult} from 'common/src/modules/shared/copilot/copilot.types';
import tool from 'common/src/modules/shared/copilot/utils/creators/tool';
import toolResult from 'common/src/modules/shared/copilot/utils/creators/toolResult';
import toolError from 'common/src/modules/shared/copilot/utils/toolError';
import {z} from 'zod';
import {AssetSelectorWidgetContent} from '../../widgets/AssetSelectorWidget/AssetSelectorWidget.types';

// Enum for source selection
enum AssetSource {
  AssetLibrary = 'AssetLibrary',
  BrandAssets = 'BrandAssets',
}

// Enum for asset type selection
enum AssetTypeSelection {
  Image = 'Image',
  Video = 'Video',
}

const inputSchema = z.object({
  source: z
    .nativeEnum(AssetSource)
    .describe('Source of assets to select from: AssetLibrary or BrandAssets'),
  assetType: z
    .nativeEnum(AssetTypeSelection)
    .describe('Type of assets to select: Image or Video'),
  maxSelectable: z
    .number()
    .min(1)
    .optional()
    .nullable()
    .describe('Maximum number of assets user can select (default: 1)'),
  message: z.string().optional().nullable().describe('Optional message to display to the user'),
});

function selectAssetsTool() {
  async function execute({
    input,
    toolUtils: {requestUserFeedback},
  }: ToolExecuteParams<typeof inputSchema>): Promise<ToolResult> {
    try {
      // Map source to AssetFolderEntityType
      const entityType =
        input.source === AssetSource.AssetLibrary
          ? AssetFolderEntityType.AssetsLibrary
          : AssetFolderEntityType.Brand;

      // Map assetType to AssetType
      const assetType =
        input.assetType === AssetTypeSelection.Image ? AssetType.Image : AssetType.Video;

      // Create widget content
      const widgetContent: AssetSelectorWidgetContent = {
        entityType,
        assetType,
        maxSelectable: input.maxSelectable ?? 1,
        message: input.message ?? undefined,
      };

      // Show blocking widget and wait for user selection
      const result = await requestUserFeedback({
        displayMode: BlockingWidgetDisplayMode.Inline,
        widget: {
          type: CopilotWidgetType.AssetSelectorWidget,
          content: widgetContent,
        },
      });

      // Parse the JSON result from widget
        const selectedAssets = JSON.parse(result);

        if (!Array.isArray(selectedAssets) || selectedAssets.length === 0) {
          return toolError('No assets selected');
        }

        // Format the result for the agent
        const assetList = selectedAssets
          .map(
            (asset: {id: number; name: string; width: number; height: number; description: string}) =>
              `ID: ${asset.id}, Name: ${asset.name}, Dimensions: ${asset.width}x${asset.height}, Description: ${asset.description || 'N/A'}`,
          )
          .join('\n');

        return toolResult(
          `Selected ${selectedAssets.length} asset${selectedAssets.length > 1 ? 's' : ''}:\n${assetList}\n\nFull details (JSON): ${result}`,
        );
       } catch (error) {
      return toolError(
        error instanceof Error ? error.message : 'Failed to select assets',
      );
    }
  }

  return tool({
    name: ToolName.SelectAssets,
    displayName: 'Selecting assets',
    description: `Allows the user to select image or video assets from Asset Library or Brand Assets. Returns selected assets with their id, name, width, height, and description. Use this tool when you need the user to choose specific assets.`,
    execute,
    inputSchema,
  });
}

export default selectAssetsTool;

